<template>
  <div class="wiki-view-page">
    <!-- Header with breadcrumb and actions -->
    <div class="wiki-view-header">
      <div class="header-left">
        <router-link :to="{ path: `/app/wiki/`}" class="back-link">
          <i class="fa fa-arrow-left"></i> Back to Wiki
        </router-link>
        <h1 class="page-title" v-if="wikiPage && wikiPage.length > 0 && wikiPage[0] && wikiPage[0][0]">
          {{ wikiPage[0][0].Name }}
        </h1>
      </div>
      <div class="header-actions">
        <router-link
          :to="{ name: 'WikiEdit', params: { wikiPageID: `${this.wikiPageID}` } }"
          class="btn btn-primary edit-btn"
        >
          <i class="fa fa-pencil"></i> Edit Page
        </router-link>
        <button type="button" class="btn btn-outline-primary" @click="gotoCreatePage()">
          <i class="fa fa-plus"></i> New Page
        </button>
      </div>
    </div>

    <!-- Page metadata -->
    <div class="wiki-meta" v-if="wikiPage && wikiPage.length > 0 && wikiPage[0] && wikiPage[0][0]">
      <div class="meta-tags" v-if="selectedTags && selectedTags.length > 0">
        <router-link
          v-for="(tag, index) in selectedTags"
          :key="index"
          :to="{ path: `/app/wiki/${tag.Tag}` }"
          class="meta-tag"
        >
          {{ tag.Tag }}
        </router-link>
      </div>
      <div class="meta-info">
        <span class="meta-item" v-if="wikiPage[0][0].UpdatedAt">
          <i class="fa fa-clock-o"></i> Updated {{ wikiPage[0][0].UpdatedAt | dateformat(dateFormatStr) }}
        </span>
        <span class="meta-item public-indicator" v-if="wikiPage[0][0].IsPublic === 1">
          <i class="fa fa-globe"></i> Public
        </span>
      </div>
    </div>

    <!-- Content area -->
    <div class="wiki-content-wrapper">
      <div class="wiki-content">
        <CustomEditor
          v-if="this.wikiPage && this.wikiPage.length > 0"
          :editorData="this.editorData"
          :editorTools="editorTools"
          :viewMode="true"
          @save="save"
          @onReady="onReady"
          save-button-id="save-button"
        />
      </div>

      <!-- Sidebar with related pages -->
      <div class="wiki-sidebar" v-if="siteTags && siteTags.length > 0">
        <div class="sidebar-section">
          <h4 class="sidebar-title">Browse by Tag</h4>
          <div class="sidebar-tags">
            <router-link
              v-for="(tag, index) in siteTags"
              :key="index"
              :to="{ path: `/app/wiki/${tag.Tag}` }"
              class="sidebar-tag"
            >
              {{ tag.Tag }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
// https://github.com/codex-team/editor.js/issues/676

import Multiselect from "vue-multiselect";
//import { Editor } from 'vue-editor-js'
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import CustomEditor from "@/components/Editor/Editor.vue";
import { mapState, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import mixin from '../../mixin.js';
// Import CustomLink component but don't use it directly
// import CustomLink from "@/components/Editor/custom-link/index.js";

  export default {
    name: 'WikiView',
    components: { Multiselect, CustomEditor, ErrorDisplay },
    props: ['wikiPageID'],
    mixins: [mixin],

    created() {
      this.init();
    },

    data() {
      return {
        wikiPage: {},
        selectedTags: [],
        editorTools: {
          header: {
            inlineToolbar: ["marker", "italic", "bold", "link"]
          },
          simpleImage: {
            inlineToolbar: true
          },
          // Use official @editorjs/image with S3 upload configuration (matched with WikiEdit/WikiCreate)
          // Note: In view mode, upload is disabled but display should work properly
          image: {
            config: {
              uploader: {
                uploadByFile(file) {
                  return new Promise(async (resolve, reject) => {
                    // In view mode, disable upload but still support image display
                    reject(new Error('Upload disabled in view mode'));
                  });
                },
                uploadByUrl(url) {
                  return new Promise((resolve) => {
                    // Simple URL validation and return for display purposes
                    if (url.match(/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
                      resolve({
                        success: 1,
                        file: {
                          url: url
                        }
                      });
                    } else {
                      resolve({
                        success: 0,
                        message: 'Invalid image URL'
                      });
                    }
                  });
                }
              }
            }
          },
          embed: {
            inlineToolbar: true,
            config: {
              services: {
                youtube: {
                  // Custom regex to handle YouTube URLs with extra query parameters
                  regex: /(?:https?:\/\/)?(?:www\.)?(?:(?:youtu\.be\/)|(?:youtube\.com)\/(?:v\/|u\/\w\/|embed\/|watch))(?:(?:\?v=)?([^#&?=\s]+))(?:[?&][^#\s]*)?/,
                  embedUrl: 'https://www.youtube.com/embed/<%= remote_id %>',
                  html: '<iframe style="width:100%;" height="320" frameborder="0" allowfullscreen></iframe>',
                  height: 320,
                  width: 600
                },
                vimeo: true,
                coub: true,
                codepen: true,
                instagram: true,
                twitter: true,
                gfycat: true,
                imgur: true,
                pinterest: true,
                github: true
              }
            }
          },
          list: {
            inlineToolbar: true
          },
          delimiter: {},
          table: {
            inlineToolbar: true
          },
          marker: {
            shortcut: "CMD+SHIFT+M"
          },
          inlineCode: {
            shortcut: "CMD+SHIFT+C"
          },
          paragraph: {
            inlineToolbar: true
          },
          checklist: {
            inlineToolbar: true
          },
          // Disabling WikiLink tool to fix error
          // wikiLink: {
          //   inlineToolbar: true
          // }
        },
        value: [],
        simpleDropdownVariant: "Select",
        isPickerActive: false
    };
  },

  methods: {
      ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
      ...mapActions('wiki', ['getWikiPage', 'updateWiki']),

      async init() {
        this.wikiPage = await this.getWikiPage({wikiPageID: this.wikiPageID, siteID: this.siteID});
      },

      addTag(newTag) {
        // Normalize + dedupe. If the user types a tag whose name already
        // exists on this site (case-insensitive, trimmed), reuse the existing
        // row instead of pushing a second entry. This prevents the client from
        // sending a "new" tag to the backend which would then create a
        // duplicate row in teamio.tags.
        const norm = String(newTag || '').trim().toLowerCase();
        if (!norm) return;
        const existing = this.siteTags.find(t => String(t.Tag || '').trim().toLowerCase() === norm);
        if (existing) {
          if (!this.selectedTags.find(t => t.TagID === existing.TagID && t.Tag === existing.Tag)) {
            this.selectedTags.push(existing);
          }
          return;
        }
        const tag = {
          Tag: String(newTag).trim(),
          TagID: '',
        };
        this.siteTags.push(tag);
        this.selectedTags.push(tag);
      },

      onSelect (option) {
        if (option === 'Disable me!') this.isDisabled = true
      },

      onChange (value) {
        this.value = value
        if (value.indexOf('Reset me!') !== -1) this.value = []
      },

      stripHtml(html)
      {
        var tmp = document.createElement("DIV");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || "";
      },

      async save(e) {
        console.log('now saving');
        let payload = {
          WikiPageID: this.wikiPageID,
          PageToUpdate : {
            Name: this.stripHtml(_.get(e, 'blocks[0].data.text')),
            Content: JSON.stringify(e),
            SiteID: this.siteID
          },
          Tags: this.selectedTags.length > 0? this.selectedTags : null,
          SiteID: this.siteID
        };
        console.log('payload:' + JSON.stringify(payload));
        await this.updateWiki(payload);
      },

      onReady: () => {
        var elements = document.querySelectorAll('[contenteditable=true]');
        elements.forEach(element => {
          element.setAttribute('contenteditable' , false);
        });
      },

      gotoCreatePage() {
        this.$router.push('/app/wikiCreate');
      },

      successCountDownChanged(count) {
        this.setDismissSuccessCountDown(count);
      },

    },

    computed: {
      ...mapState({
          loading: state => state.alert.loading,
          loaded: state => state.alert.loaded,
          errored: state => state.alert.errored,
          alertMessage: state => state.alert.message,
          dismissSuccessCountDown : state => state.alert.dismissSuccessCountDown,
          siteID: state => state.customer.siteID,
          userID: state => state.customer.userID,
          sites: state => state.customer.sites,
          employees: state => state.customer.employees,
      }),

      editorData() {
        // FIXED: Safe property access with proper null checking
        if(this.wikiPage && this.wikiPage.length > 0 && this.wikiPage[0] && this.wikiPage[0][0] && this.wikiPage[0][0].Content) {
          // load the selected tags
          let x = [];
          let wikiTags;
          let _siteTags = this.siteTags;
          //console.log('_siteTags', _siteTags);
          if(this.wikiPage && this.wikiPage.length > 0 && this.wikiPage[0][0])
            wikiTags = this.wikiPage[0][0].Tags;
          if(wikiTags) {
            //console.log('wikiTags', wikiTags);
            let tagArray = wikiTags.split(',');
            tagArray.forEach(function(tag) {
              let o = {};
              let tagIDObj = _.find(_siteTags, {'Tag': tag});
              let tagID;
              if(tagIDObj)
                tagID = tagIDObj.TagID;
              if(!tagID)
                tagID = '';
              o.TagID = tagID,
              o.Tag = tag
              x.push(o);
            });
          }
          this.selectedTags = x;
          return JSON.parse(this.wikiPage[0][0].Content);
        }
        else
          return {};
      },

      siteTags() {
        if(this.wikiPage && this.wikiPage.length > 1)
          return this.wikiPage[1];
      },
    }
  };
</script>

<style src="./WikiView.scss" lang="scss" scoped/>
<style src="./WikiViewEditor.scss" lang="scss" scoped/>
